// api/spatialApi.js – matches the new backend spatial endpoints
import { apiFetch, getAuthHeaders } from "./apiClient";
import { mockApi } from "./mockData";

// Base URL – no version prefix (the actual API uses /api/spatial/...)
const spatialBase = () => `/api/spatial`;

/**
 * 1. Get available agriculture names and crop types
 *    GET /api/spatial/filters/
 *    Response: { keshte: string[], croptype: string[] }
 */
export async function fetchSpatialFilters() {
  return apiFetch(`${spatialBase()}/filters/`, {
    method: "GET",
    headers: getAuthHeaders(),
    mockHandler: () =>
      mockApi.getSpatialFilters?.() ?? {
        keshte: [],
        croptype: [],
        types: [],
      },
  });
}

export async function fetchSpatialLevels() {
  const filters = await fetchSpatialFilters();
  return filters.types || filters.available_unit_types || ["farm", "block", "office", "management"];
}

/**
 * 2. Get variety, age, status for a given agriculture (and optional crop)
 *    GET /api/spatial/options/?keshte=<name>&croptype=<type>
 *    Response: { variety: string[], age: string[], status: string[] }
 */
export async function fetchSpatialOptions(keshte, croptype) {
  const params = new URLSearchParams();
  params.set("keshte", keshte);
  if (croptype) params.set("croptype", croptype);
  // Some servers are strict about '+' vs '%20' for spaces in query strings.
  const qs = params.toString().replace(/\+/g, "%20");
  return apiFetch(`${spatialBase()}/options/?${qs}`, {
    method: "GET",
    headers: getAuthHeaders(),
    mockHandler: () => mockApi.getSpatialOptions?.(keshte, croptype) ?? {
      variety: [],
      age: [],
      status: [],
    },
  });
}

/**
 * 3. Get available units (farms, blocks, offices, management)
 *    GET /api/spatial/units/?keshte=<name>&type=<type>&croptype=<...>&variety=<...>&age=<...>&status=<...>
 *    Response: { units: string[] }
 */
export async function fetchSpatialUnits({ keshte, type, croptype, variety, age, status }) {
  const params = new URLSearchParams();
  params.set("keshte", keshte);
  params.set("type", type);
  if (croptype) params.set("croptype", croptype);
  if (variety) params.set("variety", variety);
  if (age) params.set("age", age);
  if (status) params.set("status", status);
  const qs = params.toString().replace(/\+/g, "%20");

  return apiFetch(`${spatialBase()}/units/?${qs}`, {
    method: "GET",
    headers: getAuthHeaders(),
    mockHandler: () => mockApi.getSpatialUnits?.({ keshte, type, croptype, variety, age, status }) ?? { units: [] },
  });
}

/**
 * 4. Load polygon(s) for a specific unit matching the filter criteria
 *    POST /api/spatial/polygon/
 *    Request body: { keshte, type, unit, croptype?, variety?, age?, status? }
 *    Response: GeoJSON FeatureCollection
 */
export async function fetchSpatialPolygon(filters) {
  return apiFetch(`${spatialBase()}/polygon/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(filters),
    mockHandler: () => mockApi.getSpatialPolygon?.(filters) ?? {
      type: "FeatureCollection",
      features: [],
    },
  });
}